/**
 * Task #3454 — Commissioning Runtime v3: schemi zod dei boundary input.
 *
 * SoT: docs/commissioning-runtime-v3.md §4/§6/§7. Tutti gli schemi sono
 * .strict() (niente .passthrough()); union discriminate dove possibile;
 * numeri vincolati (nonnegative dove sensato). I tipi dei body validati sono
 * derivati con `z.infer<typeof ...>` (docs/zod-boundary.md).
 */
import { z } from 'zod';
import {
  COMMISSIONING_AGGREGATE_METRICS,
  COMMISSIONING_EFFECT_TARGETS,
  COMMISSIONING_EFFECT_VALUE_OPS,
  COMMISSIONING_RANKING_TIE_BREAKERS,
  COMMISSIONING_THRESHOLD_KINDS,
  type CommissioningRuleEffect,
  type CommissioningRuleVariant,
} from '../types/commissioning-runtime-v3';
import { COMMISSIONING_CRM_TRIGGERS } from '../types/commissioning';
import { INSTALLMENT_REPAYMENTS, PRICE_LIST_TYPES } from '../pos/pricelist';
import { PAYMENT_MODE_CREDIT_HOLDERS } from '../pos/payment-modes';
import { PRODUCT_NATURES } from '../pos/admin-inputs';
import { commissioningThresholdLevelValuesSchema, commissioningMemberThresholdsSchema } from './commissioning-declarative-thresholds';

function exclusiveThresholdSections(
  value: { thresholds?: unknown; declarativeThresholds?: unknown }, ctx: z.RefinementCtx,
) {
  if (value.thresholds != null && value.declarativeThresholds != null) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['declarativeThresholds'],
    message: 'Soglie dichiarative e soglie aggregate non possono coesistere sullo stesso livello',
  });
}

// ---------------------------------------------------------------------------
// Primitive riusabili
// ---------------------------------------------------------------------------

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data in formato YYYY-MM-DD');
const isoDateTimeSchema = z.string().datetime({ offset: true });

// ---------------------------------------------------------------------------
// §4 — Effetti tipizzati
// ---------------------------------------------------------------------------

const effectValueOpSchema = z
  .object({
    op: z.enum(COMMISSIONING_EFFECT_VALUE_OPS),
    target: z.enum(COMMISSIONING_EFFECT_TARGETS),
    value: z.number().finite(),
    priority: z.number().int().optional(),
    alternativeGroup: z.string().min(1).max(120).optional(),
  })
  .strict();

const effectZeroSchema = z
  .object({
    op: z.literal('zero'),
    target: z.enum(COMMISSIONING_EFFECT_TARGETS),
    priority: z.number().int().optional(),
    alternativeGroup: z.string().min(1).max(120).optional(),
  })
  .strict();

const effectNoopSchema = z.object({ op: z.literal('noop') }).strict();

/** Effetto finito e tipizzato (union su op). */
export const commissioningRuleEffectSchema: z.ZodType<CommissioningRuleEffect> = z.union([
  effectValueOpSchema,
  effectZeroSchema,
  effectNoopSchema,
]);

const effectsArraySchema = z.array(commissioningRuleEffectSchema).max(200);

// ---------------------------------------------------------------------------
// §4 — Aggregate rules + variante
// ---------------------------------------------------------------------------

const aggregateAccessSchema = z
  .object({
    functionId: z.string().uuid().optional(),
    minDistinctProducts: z.number().int().nonnegative().optional(),
    minCountedEvents: z.number().int().nonnegative().optional(),
  })
  .strict();

const aggregateTgtSchema = z
  .object({
    required: z.boolean(),
    unit: z.string().min(1).max(30).optional(),
  })
  .strict();

const thresholdBandSchema = z
  .object({
    from: z.number().finite(),
    to: z.number().finite().optional(),
    effects: effectsArraySchema,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.to !== undefined && v.to < v.from) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'to deve essere >= from' });
    }
  });

const aggregateThresholdsSchema = z
  .object({
    kind: z.enum(COMMISSIONING_THRESHOLD_KINDS),
    metric: z.enum(COMMISSIONING_AGGREGATE_METRICS),
    bands: z.array(thresholdBandSchema).min(1).max(100),
  })
  .strict();

const rankingPositionSchema = z
  .object({
    position: z.number().int().min(1),
    effects: effectsArraySchema,
  })
  .strict();

const aggregateRankingSchema = z
  .object({
    metric: z.enum(COMMISSIONING_AGGREGATE_METRICS),
    positions: z.array(rankingPositionSchema).min(1).max(200),
    tieBreaker: z.enum(COMMISSIONING_RANKING_TIE_BREAKERS),
  })
  .strict();

const aggregateCheckpointSchema = z
  .object({
    code: z.string().min(1).max(80),
    offsetMonths: z.number().int().optional(),
    atDate: isoDateSchema.optional(),
    functionId: z.string().uuid().optional(),
    effects: effectsArraySchema,
    elseEffects: effectsArraySchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.offsetMonths !== undefined && v.atDate !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['atDate'],
        message: 'Checkpoint: offsetMonths e atDate sono mutuamente esclusivi',
      });
    }
  });

const aggregateBonusMalusSchema = z
  .object({
    code: z.string().min(1).max(80),
    functionId: z.string().uuid(),
    effects: effectsArraySchema,
  })
  .strict();

/** Regole aggregate tipizzate dell'istanza. */
export const commissioningAggregateRulesSchema = z
  .object({
    access: aggregateAccessSchema.optional(),
    tgt: aggregateTgtSchema.optional(),
    thresholds: aggregateThresholdsSchema.optional(),
    ranking: aggregateRankingSchema.optional(),
    checkpoints: z.array(aggregateCheckpointSchema).max(50).optional(),
    bonusMalus: z.array(aggregateBonusMalusSchema).max(50).optional(),
    declarativeThresholds: commissioningThresholdLevelValuesSchema.nullable().optional(),
  })
  .strict().superRefine(exclusiveThresholdSections);

/** Variante: override A SEZIONE INTERA (access/thresholds/ranking/checkpoints/bonusMalus). */
export const commissioningRuleVariantSchema: z.ZodType<CommissioningRuleVariant> = z
  .object({
    access: aggregateAccessSchema.optional(),
    thresholds: aggregateThresholdsSchema.optional(),
    ranking: aggregateRankingSchema.optional(),
    checkpoints: z.array(aggregateCheckpointSchema).max(50).optional(),
    bonusMalus: z.array(aggregateBonusMalusSchema).max(50).optional(),
    declarativeThresholds: commissioningThresholdLevelValuesSchema.nullable().optional(),
    memberThresholds: commissioningMemberThresholdsSchema.optional(),
  })
  .strict().superRefine(exclusiveThresholdSections);

// ---------------------------------------------------------------------------
// §7 — Competition cluster (manuale e from-model)
// ---------------------------------------------------------------------------

const CLUSTER_ENTITY_TYPES = ['rs', 'pdv', 'risorsa'] as const;
const clusterUuid = z.string().uuid().transform((value) => value.toLowerCase());
const contextRevision = z.number().int().min(1).max(2147483647);

/** Current response rows. Historical snapshot normalization belongs to its reader. */
export const commissioningCompetitionClusterDtoSchema = z.object({
  id: clusterUuid, tenant_id: clusterUuid, competition_id: clusterUuid,
  source_cluster_id: clusterUuid.nullable(), name: z.string(), description: z.string().nullable(),
  entity_type: z.enum(CLUSTER_ENTITY_TYPES), business_driver_id: clusterUuid.nullable(),
  configurator_instance_id: clusterUuid.nullable(), created_at: z.string(), updated_at: z.string(),
}).strict();
export const commissioningCompetitionClusterMemberDtoSchema = z.object({
  id: clusterUuid, tenant_id: clusterUuid, competition_cluster_id: clusterUuid, competition_id: clusterUuid,
  company_id: clusterUuid.nullable(), store_id: clusterUuid.nullable(), user_id: clusterUuid.nullable(),
  source_member_id: clusterUuid.nullable(), business_driver_id: clusterUuid.nullable(),
  configurator_instance_id: clusterUuid.nullable(), economic_subject_id: clusterUuid.nullable(),
  is_active: z.boolean(), created_at: z.string(), updated_at: z.string(),
}).strict();

function validateClassificationIdentity(
  value: { company_id?: string | null; store_id?: string | null; user_id?: string | null; economic_subject_id?: string | null; source_member_id?: string | null },
  ctx: z.RefinementCtx,
): void {
  const entities = [value.company_id, value.store_id, value.user_id].filter((id) => id != null);
  if (value.economic_subject_id != null) {
    if (entities.length || value.source_member_id != null) ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Il soggetto economico deriva dal roster: non inviare identità personali o provenienze alternative',
    });
  } else if (entities.length !== 1) ctx.addIssue({
    code: z.ZodIssueCode.custom, message: 'Seleziona un soggetto economico oppure una sola identità diretta (ragione sociale, sede o persona)',
  });
}
function validateClassificationAxis(
  value: { business_driver_id?: string | null; configurator_instance_id?: string | null }, ctx: z.RefinementCtx,
): void {
  if (value.business_driver_id != null && value.configurator_instance_id != null) ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['configurator_instance_id'],
    message: 'L’asse è overall, driver oppure configuratore: non due assi contemporaneamente',
  });
}

/** Membro del cluster gara: entità esclusiva (exactly-one via superRefine). */
export const createCommissioningCompetitionClusterMemberSchema = z
  .object({
    company_id: clusterUuid.nullable().optional(),
    store_id: clusterUuid.nullable().optional(),
    user_id: clusterUuid.nullable().optional(),
    source_member_id: clusterUuid.nullable().optional(),
    economic_subject_id: clusterUuid.nullable().optional(),
  })
  .strict()
  .superRefine(validateClassificationIdentity);

/**
 * Asse del cluster gara: null = OVERALL, altrimenti un driver ammesso dalla
 * gara. Obbligatorio in input (scelta esplicita, mai implicita).
 */
const competitionClusterAxisSchema = clusterUuid.nullable();

/** Creazione cluster gara: manuale (entity_type) OPPURE da modello (sourceClusterId). */
export const createCompetitionClusterSchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('manual'),
      name: z.string().min(1).max(160),
      description: z.string().max(4000).nullable().optional(),
      entity_type: z.enum(CLUSTER_ENTITY_TYPES),
      business_driver_id: competitionClusterAxisSchema,
      configurator_instance_id: competitionClusterAxisSchema.optional(),
      expected_context_revision: contextRevision.optional(),
      confirm_revision_impact: z.boolean().optional(),
      members: z.array(createCommissioningCompetitionClusterMemberSchema).max(5000).optional(),
    })
    .strict(),
  z
    .object({
      mode: z.literal('from_model'),
      sourceClusterId: z.string().uuid(),
      /** Override opzionale del nome della fotografia (default = nome modello). */
      name: z.string().min(1).max(160).optional(),
      business_driver_id: competitionClusterAxisSchema,
      configurator_instance_id: competitionClusterAxisSchema.optional(),
      expected_context_revision: contextRevision.optional(),
      confirm_revision_impact: z.boolean().optional(),
      collective_subject_id: clusterUuid.optional(),
    })
    .strict(),
]).superRefine(validateClassificationAxis);

/**
 * Aggiornamento cluster gara (name/description; entity_type e asse driver
 * immutabili se il cluster ha membri o assegnazioni: trigger DB).
 */
export const updateCompetitionClusterSchema = z
  .object({
    name: z.string().min(1).max(160).optional(),
    description: z.string().max(4000).nullable().optional(),
    business_driver_id: competitionClusterAxisSchema.optional(),
    configurator_instance_id: competitionClusterAxisSchema.optional(),
    expected_context_revision: contextRevision.optional(),
    confirm_revision_impact: z.boolean().optional(),
  })
  .strict().superRefine(validateClassificationAxis);

/** Destinazione di una cella della matrice entità × asse. */
export const competitionClusterMatrixTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('cluster'), clusterId: z.string().uuid() }).strict(),
  z.object({ kind: z.literal('new_cluster'), name: z.string().min(1).max(160) }).strict(),
  z.object({ kind: z.literal('unassigned') }).strict(),
]);

/** Cella da applicare: entità (exactly-one) × asse → destinazione. */
export const competitionClusterMatrixCellInputSchema = z
  .object({
    company_id: clusterUuid.nullable().optional(),
    store_id: clusterUuid.nullable().optional(),
    user_id: clusterUuid.nullable().optional(),
    economic_subject_id: clusterUuid.nullable().optional(),
    business_driver_id: competitionClusterAxisSchema,
    configurator_instance_id: competitionClusterAxisSchema.optional(),
    target: competitionClusterMatrixTargetSchema,
  })
  .strict()
  .superRefine(validateClassificationIdentity).superRefine(validateClassificationAxis);

/**
 * Applicazione massiva della matrice (POST /competitions/:id/cluster-matrix/apply):
 * transazionale, ogni cella sposta/crea/rimuove la membership dell'entità
 * sull'asse indicato. Fail-closed se il cluster destinazione è di un asse
 * diverso dalla cella.
 */
export const applyCompetitionClusterMatrixSchema = z
  .object({
    cells: z.array(competitionClusterMatrixCellInputSchema).min(1).max(20000),
    expected_context_revision: contextRevision.optional(),
    confirm_revision_impact: z.boolean().optional(),
  })
  .strict();

/** Aggiunta massiva membri (bulk). */
export const addCompetitionClusterMembersSchema = z
  .object({
    members: z.array(createCommissioningCompetitionClusterMemberSchema).min(1).max(5000),
    expected_context_revision: contextRevision.optional(),
    confirm_revision_impact: z.boolean().optional(),
  })
  .strict();

/** DELETE keeps the revision in the query so existing HTTP clients need no body. */
export const competitionContextMutationQuerySchema = z.object({
  expected_context_revision: z.coerce.number().int().positive().max(2_147_483_647).optional(),
  /** Active-competition revision: the caller has read the impact preview and confirms it. */
  confirm_revision_impact: z.enum(['true', 'false']).optional().transform((value) => value === 'true'),
}).strict();

// ---------------------------------------------------------------------------
// §7 — Assignments (bulk tipizzato: assignments + drivers + varianti)
// ---------------------------------------------------------------------------

const assignmentDriverInputSchema = z
  .object({
    business_driver_id: z.string().uuid(),
    tgt_standard: z.number().nonnegative().nullable().optional(),
    tgt_unit: z.string().min(1).max(30).nullable().optional(),
    rule_variant: commissioningRuleVariantSchema.nullable().optional(),
  })
  .strict().superRefine((value, ctx) => {
    if (value.rule_variant?.memberThresholds !== undefined) ctx.addIssue({
      code: z.ZodIssueCode.custom, path: ['rule_variant', 'memberThresholds'],
      message: 'Le bande personali appartengono all’assegnazione, non al driver',
    });
  });

const assignmentInputSchema = z
  .object({
    competition_cluster_id: z.string().uuid(),
    rule_variant: commissioningRuleVariantSchema.nullable().optional(),
    is_active: z.boolean().optional(),
    drivers: z.array(assignmentDriverInputSchema).max(500).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.drivers) {
      const ids = new Set(v.drivers.map((d) => d.business_driver_id));
      if (ids.size !== v.drivers.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['drivers'],
          message: 'business_driver_id duplicato nella stessa assignment',
        });
      }
    }
  });

/** Sostituzione integrale delle assegnazioni dell'istanza (bulk transazionale). */
export const replaceAssignmentsSchema = z
  .object({
    assignments: z.array(assignmentInputSchema).max(500),
    expectedRevision: z.string().min(1).max(100).optional(),
    recalc_ack: z.boolean().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const ids = new Set(v.assignments.map((a) => a.competition_cluster_id));
    if (ids.size !== v.assignments.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assignments'],
        message: 'competition_cluster_id duplicato nel payload',
      });
    }
  });

// ---------------------------------------------------------------------------
// §7 — TGT update (standard + bulk override membri)
// ---------------------------------------------------------------------------

const memberTgtOverrideInputSchema = z
  .object({
    memberId: z.string().uuid(),
    /** null = rimuove l'override e ripristina il TGT standard. */
    tgtValue: z.number().nonnegative().nullable(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .strict();

/** Aggiornamento TGT del driver: standard + override massivi per membro. */
export const updateAssignmentDriverTgtSchema = z
  .object({
    tgt_standard: z.number().nonnegative().nullable().optional(),
    tgt_unit: z.string().min(1).max(30).nullable().optional(),
    overrides: z.array(memberTgtOverrideInputSchema).max(5000).optional(),
    expectedRevision: z.string().min(1).max(100).optional(),
    recalc_ack: z.boolean().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.overrides) {
      const ids = new Set(v.overrides.map((o) => o.memberId));
      if (ids.size !== v.overrides.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['overrides'],
          message: 'memberId duplicato negli override',
        });
      }
    }
  });

// ---------------------------------------------------------------------------
// §7 — Value package bases (replace transazionale, unicità productId)
// ---------------------------------------------------------------------------

const valuePackageBaseInputSchema = z
  .object({
    /** NULL = base evento a livello driver (es. CRM). */
    product_id: z.string().uuid().nullable().optional(),
    label: z.string().max(200).nullable().optional(),
    valenza: z.number().nullable().optional(),
    gettone_contrattuale: z.number().nullable().optional(),
    gettone_gara: z.number().nullable().optional(),
    canone_multiplier: z.number().nullable().optional(),
    canone_inherited: z.boolean().optional(),
    canone_override: z.number().nonnegative().nullable().optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const inherited = v.canone_inherited !== false;
    const hasOverride = v.canone_override !== null && v.canone_override !== undefined;
    if (!inherited && !hasOverride) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['canone_override'],
        message: 'canone_override obbligatorio quando canone_inherited=false',
      });
    }
  });

/** Sostituzione integrale delle basi del pacchetto (unicità productId nel payload). */
export const replaceValuePackageBasesSchema = z
  .object({
    bases: z.array(valuePackageBaseInputSchema).max(5000),
  })
  .strict()
  .superRefine((v, ctx) => {
    const seen = new Set<string>();
    v.bases.forEach((b, idx) => {
      // NULL productId = base a livello driver: unica come le altre chiavi.
      const key = b.product_id ?? '__null__';
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bases', idx, 'product_id'],
          message: 'product_id duplicato nel payload (una sola base per prodotto)',
        });
      }
      seen.add(key);
    });
  });

// ---------------------------------------------------------------------------
// §6 — Simulazione: evento sintetico tipizzato
// ---------------------------------------------------------------------------

const eventAddonSnapshotSchema = z
  .object({
    addonId: z.string().uuid(),
    addonName: z.string().min(1).max(200),
    versionId: z.string().uuid(),
    version: z.number().int().min(1),
    values: z.record(
      z.string().min(1).max(80),
      z.union([z.string(), z.number(), z.boolean(), z.array(z.string()).max(200)]),
    ),
  })
  .strict();

const sellOutEventPayloadSchema = z
  .object({
    kind: z.literal('sell_out'),
    saleId: z.string().uuid(),
    saleDetailId: z.string().uuid().nullable(),
    saleNumber: z.string().max(120).nullable(),
    productId: z.string().uuid().nullable(),
    productName: z.string().max(300).nullable(),
    categoryId: z.string().uuid().nullable(),
    productTypeId: z.string().uuid().nullable(),
    quantity: z.number().nonnegative(),
    unitPrice: z.number(),
    lineTotal: z.number(),
    priceListId: z.string().uuid().nullable(),
    priceListName: z.string().max(200).nullable(),
    monthlyFee: z.number().nullable(),
    paymentMethodCode: z.string().max(80).nullable(),
    customerId: z.string().uuid().nullable(),
    // Campi introdotti dal payload sell-out canonico (sconto %, contributo
    // di ingresso, durata contratto): nullable con default null così i
    // payload sintetici esistenti restano validi.
    discountPercent: z.number().nullable().default(null),
    entryFee: z.number().nullable().default(null),
    // Task #4322 — struttura di credito (snapshot riga vendita). I payload
    // storici portano solo `installmentMethod` (legacy, stesso dominio di
    // `repaymentMethod`): accettato in lettura e ripiegato dal runtime.
    paymentModeCode: z.string().max(64).nullable().default(null),
    /** @deprecated payload storici: titolare credito (ignorato dal runtime, mai più scritto). */
    creditHolder: z.enum(PAYMENT_MODE_CREDIT_HOLDERS).nullable().optional(),
    repaymentMethod: z.enum(INSTALLMENT_REPAYMENTS).nullable().default(null),
    financingPartnerId: z.string().uuid().nullable().default(null),
    financedAmount: z.number().nullable().default(null),
    installmentsCount: z.number().int().nullable().default(null),
    /** @deprecated payload storici pre-#4322 (≡ repaymentMethod). */
    installmentMethod: z.enum(INSTALLMENT_REPAYMENTS).nullable().optional(),
    contractDurationMonths: z.number().int().nullable().default(null),
    // Task #4332 — natura anagrafica della riga (payload storici → null).
    productNature: z.enum(PRODUCT_NATURES).nullable().optional(),
    // Busta eventi gara — identità fotografate (prodotto, listino, riga
    // risolta): nullable e opzionali (assente ≡ null, come nel contratto
    // condiviso), così i payload sintetici attuali restano validi e i nuovi
    // campi sono accettati. Il set canali resta array ordinato o null (mai `[]`).
    // `productSupplierId` è anche il fatto del gate fornitore della testata.
    productSupplierId: z.string().uuid().nullable().optional(),
    productBrandId: z.string().uuid().nullable().optional(),
    productRetailBrandId: z.string().uuid().nullable().optional(),
    priceListRetailBrandId: z.string().uuid().nullable().optional(),
    priceListSupplierId: z.string().uuid().nullable().optional(),
    priceListType: z.enum(PRICE_LIST_TYPES).nullable().optional(),
    priceListChannelIds: z.array(z.string().uuid()).min(1).max(200).nullable().optional(),
    saleRetailBrandId: z.string().uuid().nullable().optional(),
    saleChannelId: z.string().uuid().nullable().optional(),
    composition: z
      .object({
        compositionProductId: z.string().uuid(),
        compositionId: z.string().uuid().optional(),
        componentCount: z.number().int().nonnegative(),
        componentProductTypeIds: z.array(z.string().uuid()).max(200),
        componentProductIds: z.array(z.string().uuid()).max(200).optional(),
        componentNatures: z.array(z.enum(PRODUCT_NATURES)).max(3).optional(),
        hasCanvass: z.boolean().optional(),
        canvassProductId: z.string().uuid().nullable().optional(),
      })
      .strict()
      .nullable(),
    addons: z.array(eventAddonSnapshotSchema).max(200),
  })
  .strict();

const crmEventPayloadSchema = z
  .object({
    kind: z.literal('crm'),
    trigger: z.enum(COMMISSIONING_CRM_TRIGGERS),
    campaignId: z.string().uuid(),
    campaignName: z.string().max(300).nullable(),
    leadId: z.string().uuid().nullable(),
    customerId: z.string().uuid().nullable(),
    personId: z.string().uuid().nullable(),
    stageId: z.string().uuid().nullable(),
    formId: z.string().uuid().nullable(),
    appointmentId: z.string().uuid().nullable(),
    massActionId: z.string().uuid().nullable(),
  })
  .strict();

const sellInEventPayloadSchema = z
  .object({
    kind: z.literal('sell_in'),
    documentType: z.enum(['ddt_passivo', 'purchase_order']),
    documentId: z.string().uuid(),
    documentNumber: z.string().max(120).nullable(),
    documentDate: isoDateSchema.nullable(),
    receivedAt: isoDateTimeSchema,
    supplierId: z.string().uuid().nullable(),
    supplierName: z.string().max(300).nullable(),
    warehouseId: z.string().uuid().nullable(),
    productId: z.string().uuid().nullable(),
    productName: z.string().max(300).nullable(),
    quantity: z.number().nonnegative(),
    /** `null` = prezzo riga assente sul documento (→ missing nel kernel), mai 0. */
    unitCost: z.number().nullable(),
    /** Netto riga (quantità × prezzo × (1 − sconto%/100)); `null` se il prezzo è assente. */
    lineTotal: z.number().nullable(),
    /** Sconto % e lordo di riga: assenti nei payload storici (≡ null). */
    discountPercent: z.number().min(0).max(100).nullable().optional(),
    grossLineTotal: z.number().nullable().optional(),
    // Busta eventi gara — fatti del prodotto e riga risolta (insegna/canale):
    // nullable e opzionali, assente ≡ null (payload sintetici attuali restano validi).
    categoryId: z.string().uuid().nullable().optional(),
    productTypeId: z.string().uuid().nullable().optional(),
    productNature: z.enum(PRODUCT_NATURES).nullable().optional(),
    productBrandId: z.string().uuid().nullable().optional(),
    productSupplierId: z.string().uuid().nullable().optional(),
    productRetailBrandId: z.string().uuid().nullable().optional(),
    retailBrandId: z.string().uuid().nullable().optional(),
    channelId: z.string().uuid().nullable().optional(),
  })
  .strict();

/** Payload evento sintetico (union discriminata su kind). */
export const commissioningSyntheticEventPayloadSchema = z.discriminatedUnion('kind', [
  sellOutEventPayloadSchema,
  crmEventPayloadSchema,
  sellInEventPayloadSchema,
]);

/**
 * Input della simulazione dry-run (evento sintetico + assi espliciti + as-of).
 *
 * Assi espliciti (store/company/user + business driver): guidano la selezione
 * membro+driver nel dry-run. Se l'utente NON li passa, l'engine sceglie
 * deterministicamente il PRIMO membro del primo cluster assegnato e il PRIMO
 * driver dell'assignment (documentato nel trace selection.explanation), così
 * la selezione risolve una configurazione reale invece di dare not_applicable.
 */
export const simulateInstanceSchema = z
  .object({
    event: commissioningSyntheticEventPayloadSchema,
    /** Explicit replay of a fully captured recipe; omission = current published configuration. */
    ruleVersion: z.number().int().positive().optional(),
    /** Data as-of (occurred_at) per la risoluzione di versioni/basi/checkpoint. */
    occurredAt: isoDateTimeSchema.optional(),
    /** Asse PDV: se assente, default deterministico dal primo membro assegnato. */
    storeId: z.string().uuid().optional(),
    /** Asse RS: se assente, default deterministico dal primo membro assegnato. */
    companyId: z.string().uuid().optional(),
    /** Asse risorsa: se assente, default deterministico dal primo membro assegnato. */
    userId: z.string().uuid().optional(),
    /**
     * Business driver dell'evento sintetico. Se assente, default deterministico
     * al primo driver dell'assignment risolto.
     */
    businessDriverId: z.string().uuid().optional(),
    /** Prodotto opzionale dell'evento (override del productId del payload). */
    productId: z.string().uuid().optional(),
    /** Data evento (YYYY-MM-DD); default = occurredAt o oggi lato engine. */
    eventDate: isoDateSchema.optional(),
    /** Quantità opzionale dell'evento sintetico. */
    quantity: z.number().nonnegative().optional(),
    /** Importo opzionale dell'evento sintetico. */
    amount: z.number().finite().optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Tipi derivati (z.infer — MAI duplicati a mano)
// ---------------------------------------------------------------------------

export type CommissioningRuleEffectInput = z.infer<typeof commissioningRuleEffectSchema>;
export type CommissioningAggregateRulesInput = z.infer<typeof commissioningAggregateRulesSchema>;
export type CommissioningRuleVariantInput = z.infer<typeof commissioningRuleVariantSchema>;
export type CreateCommissioningCompetitionClusterMemberInput = z.infer<
  typeof createCommissioningCompetitionClusterMemberSchema
>;
export type CreateCompetitionClusterInput = z.infer<typeof createCompetitionClusterSchema>;
export type UpdateCompetitionClusterInput = z.infer<typeof updateCompetitionClusterSchema>;
export type AddCompetitionClusterMembersInput = z.infer<typeof addCompetitionClusterMembersSchema>;
export type CompetitionClusterMatrixTarget = z.infer<typeof competitionClusterMatrixTargetSchema>;
export type CompetitionClusterMatrixCellInput = z.infer<typeof competitionClusterMatrixCellInputSchema>;
export type ApplyCompetitionClusterMatrixInput = z.infer<typeof applyCompetitionClusterMatrixSchema>;
export type ReplaceAssignmentsInput = z.infer<typeof replaceAssignmentsSchema>;
export type CommissioningAssignmentInput = z.infer<typeof assignmentInputSchema>;
export type CommissioningAssignmentDriverInput = z.infer<typeof assignmentDriverInputSchema>;
export type UpdateAssignmentDriverTgtInput = z.infer<typeof updateAssignmentDriverTgtSchema>;
export type CommissioningMemberTgtOverrideInput = z.infer<typeof memberTgtOverrideInputSchema>;
export type ReplaceValuePackageBasesInput = z.infer<typeof replaceValuePackageBasesSchema>;
export type CommissioningValuePackageBaseInput = z.infer<typeof valuePackageBaseInputSchema>;
export type CommissioningSyntheticEventPayloadInput = z.infer<
  typeof commissioningSyntheticEventPayloadSchema
>;
export type SimulateInstanceInput = z.infer<typeof simulateInstanceSchema>;
